import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { bookingTebox } from '../styles/tebox-booking';
import { normalizeToPlatformUiFontName } from '../../../web/src/utils/fontFamily';

// Font categories for proper fallback
const FONT_CATEGORIES: Record<string, string> = {
  'Inter': 'sans-serif',
  'DM Sans': 'sans-serif',
  'Castoro': 'serif',
  'Playfair Display': 'serif',
  'Lora': 'serif',
  'Merriweather': 'serif',
  'Libre Baskerville': 'serif',
  'PT Serif': 'serif',
  'Roboto': 'sans-serif',
  'Open Sans': 'sans-serif',
  'Montserrat': 'sans-serif',
  'Poppins': 'sans-serif',
  'Nunito': 'sans-serif',
  'JetBrains Mono': 'monospace',
  'Source Code Pro': 'monospace',
  'Fira Code': 'monospace',
  'IBM Plex Mono': 'monospace',
};

const getFontFamilyCSS = (fontFamily: string): string => {
  const normalized = normalizeToPlatformUiFontName(fontFamily);
  const category = FONT_CATEGORIES[normalized] || 'sans-serif';
  return `'${normalized}', ${category}`;
};

export interface BrandTheme {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  buttonTextColor: string;
  headerColor: string;
  borderColor: string;
  fontFamily: string;
  logoUrl?: string;
  customCss?: string;
}

interface ThemeContextType {
  theme: BrandTheme;
  setTheme: (theme: BrandTheme) => void;
  applyTheme: () => void;
}

/** Context defaults for `useTheme()`; DOM `--brand-*` vars come from `brand-settings` (club API/cache). */
export const bookingBrandThemeDefaults: BrandTheme = {
  primaryColor: '#000000',
  secondaryColor: bookingTebox.colors.textSecondary,
  accentColor: '#000000',
  backgroundColor: '#FFFFFF',
  textColor: '#021B1B',
  buttonColor: '#000000',
  buttonTextColor: '#FFFFFF',
  headerColor: bookingTebox.colors.bgPrimary,
  borderColor: bookingTebox.colors.border,
  fontFamily: 'Inter',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: BrandTheme;
}

/** Push preview/context theme onto `:root` (URL sandbox only — club branding uses `brandSettingsService`). */
export function applyBrandThemeToDocument(next: BrandTheme): void {
  const root = document.documentElement;

  root.style.setProperty('--brand-primary', next.primaryColor);
  root.style.setProperty('--brand-secondary', next.secondaryColor);
  root.style.setProperty('--brand-accent', next.accentColor);
  root.style.setProperty('--brand-background', next.backgroundColor);
  root.style.setProperty('--brand-text', next.textColor);
  root.style.setProperty('--brand-button', next.buttonColor);
  root.style.setProperty('--brand-button-text', next.buttonTextColor);
  root.style.setProperty('--brand-header', next.headerColor);
  root.style.setProperty('--brand-border', next.borderColor);
  root.style.setProperty('--brand-font', getFontFamilyCSS(next.fontFamily));

  if (next.customCss) {
    const existingStyle = document.getElementById('custom-brand-css');
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');
    style.id = 'custom-brand-css';
    style.textContent = next.customCss;
    document.head.appendChild(style);
  }
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ 
  children, 
  initialTheme = bookingBrandThemeDefaults 
}) => {
  const [theme, setTheme] = useState<BrandTheme>(initialTheme);

  const applyTheme = () => {
    applyBrandThemeToDocument(theme);
  };

  /**
   * Do **not** sync `:root` on every `theme` change — default theme would overwrite club `--brand-*`
   * applied synchronously from localStorage/API in OnlineBookingEngine (same tick → wrong font until refetch).
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');

    if (themeParam) {
      try {
        const urlTheme = JSON.parse(decodeURIComponent(themeParam)) as Partial<BrandTheme>;
        const merged = { ...bookingBrandThemeDefaults, ...urlTheme };
        setTheme(merged);
        applyBrandThemeToDocument(merged);
      } catch (error) {
        console.error('Error parsing theme from URL:', error);
      }
    }
  }, []);

  const value: ThemeContextType = {
    theme,
    setTheme,
    applyTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Hook to get themed styles
export const useThemedStyles = () => {
  const { theme } = useTheme();

  return {
    primary: {
      color: theme.primaryColor,
      backgroundColor: theme.primaryColor
    },
    secondary: {
      color: theme.secondaryColor,
      backgroundColor: theme.secondaryColor
    },
    accent: {
      color: theme.accentColor,
      backgroundColor: theme.accentColor
    },
    background: {
      color: theme.textColor,
      backgroundColor: theme.backgroundColor
    },
    text: {
      color: theme.textColor
    },
    button: {
      color: theme.buttonTextColor,
      backgroundColor: theme.buttonColor,
      borderColor: theme.buttonColor
    },
    header: {
      color: theme.textColor,
      backgroundColor: theme.headerColor
    },
    border: {
      borderColor: theme.borderColor
    }
  };
};
